import { useControllableProp } from '@chakra-ui/react';
import { Decimal } from 'decimal.js';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { OverrideProperties } from 'type-fest';

import { PREFERRED_UNIT_SYSTEMS } from '@frontend/api';
import {
  Input,
  InputGroup,
  InputGroupProps,
  InputProps,
  InputRightAddon,
} from '@frontend/components';
import { fullstoryEvent } from '@frontend/services';

import {
  MEASURE_TO_DIMENSION,
  Measure,
  UNITS_BY_SYSTEM_AND_MEASURE,
} from 'constants/unitOfMeasure';

import { inchesToMm, mmToInches } from 'utils/helpers';
import { usePreferredUnitSystem } from 'utils/measurement';

import { MathInputProvider, useMathInputProvider } from './context';
import { evaluate, getCleanUnit } from './utils';

export type MathInputFieldProps = OverrideProperties<
  InputProps,
  {
    step?: number;
    min?: number;
    max?: number;
    value?: string | null;
  }
> & {
  dynamicPrecision?: boolean;
};

/**
 * A React input component for mathematical expressions and unit conversions.
 *
 * `MathInputField` allows users to enter mathematical expressions, automatically evaluates them,
 * and handles unit conversions based on the preferred unit system (metric or imperial).
 * It supports both controlled and uncontrolled usage, and notifies parent components of value changes.
 *
 * @component
 * @param {InputProps} props - The input props, including optional `value`, `onBlur`, and other standard input attributes.
 * @param {React.Ref<HTMLInputElement>} ref - A forwarded ref to the underlying input element.
 *
 * @remarks
 * - Evaluates mathematical expressions on blur and updates the value accordingly.
 * - Converts values between metric and imperial units when the preferred unit system changes.
 * - Notifies parent components of value changes via `onChange`.
 */
export const MathInputField = forwardRef<HTMLInputElement, MathInputFieldProps>(
  function MathInputField(
    {
      onBlur: _onBlur,
      onChange: _onChange,
      value: valueProp,
      defaultValue: defaultValueProp,
      step = 1,
      min,
      max,
      dynamicPrecision = true,
      ...inputProps
    }: MathInputFieldProps,
    forwardedRef,
  ) {
    // Internal value is always stored in millimeters
    const [innerValueMm, setInnerValueMm] = useState<string | undefined>();
    const [displayPrecision, setDisplayPrecision] = useState<number | null>(
      null,
    );
    // Track previous valueProp to detect external changes
    const prevValuePropRef = useRef<string | null | undefined>(valueProp);
    // Track if the last change was from user input (to prevent re-sync loop)
    const isUserInputRef = useRef(false);

    const { preferredUnitSystem, measure, hasUnits } = useMathInputProvider();
    const dimension = MEASURE_TO_DIMENSION[measure];

    const [isControlled, value] = useControllableProp(valueProp, innerValueMm);

    const getShouldConvert = useCallback(
      (value: string): boolean => {
        if (!value || !hasUnits) return false;

        const precision = value.split('.')[1]?.length || 0;
        const testValue = value[0] === '.' ? `0${value}` : value;

        if (Number(value).toFixed(precision) !== testValue) {
          return false;
        } else {
          return true;
        }
      },
      [hasUnits],
    );

    // Convert mm value to display value based on preferred unit system
    const getDisplayValue = useCallback(
      (mmValue: string | null | undefined): string | undefined => {
        const value = mmValue?.toString();
        const shouldConvert = getShouldConvert(value ?? '');

        if (
          shouldConvert &&
          preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL
        ) {
          const convertedValue = mmToInches(Number(value), dimension);
          const formattedValue =
            displayPrecision == null
              ? convertedValue.toString()
              : convertedValue.toFixed(displayPrecision);
          return formattedValue;
        } else {
          return value;
        }
      },
      [preferredUnitSystem, dimension, getShouldConvert, displayPrecision],
    );

    // Convert value to mm based on preferred unit system
    const convertToMm = useCallback(
      (value: string): string => {
        const shouldConvert = getShouldConvert(value);

        if (
          shouldConvert &&
          preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL
        ) {
          return inchesToMm(Number(value), dimension).toString();
        } else {
          return value;
        }
      },
      [preferredUnitSystem, dimension, getShouldConvert],
    );

    // Convert defaultValue to mm if provided
    const convertedDefaultValue = useMemo(
      () => convertToMm(defaultValueProp?.toString() ?? ''),
      [defaultValueProp, convertToMm],
    );

    // Sync internal state when controlled value changes (e.g., Formik reinitialization)
    // This ensures the component properly updates when the parent reinitializes with new values
    useEffect(() => {
      if (isControlled) {
        // Check if valueProp changed or if we need to initialize innerValueMm
        const valueChanged = valueProp !== prevValuePropRef.current;
        const needsInitialization =
          innerValueMm === undefined && valueProp !== undefined;

        if (valueChanged || needsInitialization) {
          // Skip sync if this change was caused by user input (prevents feedback loop)
          // Only sync for external changes like Formik reinitialization
          if (isUserInputRef.current) {
            isUserInputRef.current = false;
            prevValuePropRef.current = valueProp;
            return;
          }

          // When controlled, sync innerValueMm to match the controlled value
          const mmValue = valueProp?.toString() ?? '';
          setInnerValueMm(mmValue);
          if (dynamicPrecision) {
            // Reset display precision when value changes externally
            // This ensures the display value recalculates correctly for the new value
            setDisplayPrecision(null);
          }
          // Update ref to track this value for next comparison
          prevValuePropRef.current = valueProp;
        }
      }
    }, [isControlled, valueProp, innerValueMm, dynamicPrecision]);

    // The display value shown in the input
    const displayValue = getDisplayValue(value);

    const updateValueAndNotify = useCallback(
      (newDisplayValue: string) => {
        // Mark that this change originated from user input
        isUserInputRef.current = true;

        // Convert display value to mm for internal storage and onChange
        const newMmValue = convertToMm(newDisplayValue);

        // save current precision for display purposes after updates
        if (dynamicPrecision) {
          const newDisplayPrecision =
            newDisplayValue.split('.')[1]?.length || 0;
          if (preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL) {
            if (newDisplayPrecision !== displayPrecision) {
              setDisplayPrecision(newDisplayPrecision);
            }
          } else {
            setDisplayPrecision(null);
          }
        }

        if (!isControlled) {
          setInnerValueMm(newMmValue);
        }

        // Always pass mm value to onChange
        _onChange?.({
          target: {
            value: newMmValue,
            name: inputProps.name,
            id: inputProps.id,
          },
        } as React.ChangeEvent<HTMLInputElement>);
      },
      [
        convertToMm,
        dynamicPrecision,
        displayPrecision,
        preferredUnitSystem,
        isControlled,
        _onChange,
        inputProps.name,
        inputProps.id,
      ],
    );
    // No need for unit system change effect since we always store in mm
    // The display value will automatically update when preferredUnitSystem changes
    // through the getDisplayValue function
    const unit = getCleanUnit(
      UNITS_BY_SYSTEM_AND_MEASURE[preferredUnitSystem][measure],
    );

    const doEvaluation = useCallback(
      (displayValue: string) => {
        const expression = displayValue;

        try {
          const result = evaluate(expression);
          let finalDisplayValue: number | undefined;

          if (result instanceof Decimal) {
            if (result.toString() !== displayValue) {
              fullstoryEvent('mathInputDidMath', {
                expression,
                result: result.toString(),
              });
            }
            finalDisplayValue = Number(result.toString());
          } else if (hasUnits) {
            if (result.units[0].unit.name !== unit) {
              fullstoryEvent('mathInputUnitConversion', {
                expression,
                unit,
                result: result.toString(),
              });
            } else {
              fullstoryEvent('mathInputDidMath', {
                expression,
                result: result.toString(),
              });
            }
            finalDisplayValue = Number(result.toNumeric(unit));
          }

          if (
            !!finalDisplayValue &&
            finalDisplayValue !== Number(displayValue)
          ) {
            updateValueAndNotify(finalDisplayValue.toString());
            return finalDisplayValue;
          }
        } catch (error) {
          if (error instanceof Error) {
            fullstoryEvent('mathInputEvaluationError', {
              expression,
              error: error.message,
            });
          }
          console.error('Failed to evaluate expression:', error);
        }
      },
      [unit, hasUnits, updateValueAndNotify],
    );

    const handleArrowKeys = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        const currentDisplayValue = Number(displayValue || 0);
        if (isNaN(currentDisplayValue)) return;

        let stepValue =
          step ?? preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.METRIC
            ? 0.1
            : 0.0625;

        // Handle modifiers
        if (e.shiftKey) {
          stepValue = stepValue * 10;
        } else if (e.altKey || e.metaKey) {
          stepValue = stepValue / 10;
        }

        // Helper to handle floating point precision
        const roundToPrecision = (num: number, precision: number) => {
          const factor = Math.pow(10, precision);
          return Math.round(num * factor) / factor;
        };

        // Get precision from both current value and step value
        const valuePrecision =
          displayValue?.toString().split('.')[1]?.length || 0;
        const stepPrecision = stepValue.toString().split('.')[1]?.length || 0;
        const precision = Math.max(valuePrecision, stepPrecision);

        // Handle arrow keys
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const newDisplayValue = roundToPrecision(
            currentDisplayValue + stepValue,
            precision,
          );
          // Convert to mm for min/max comparison
          const newMmValue = Number(convertToMm(newDisplayValue.toString()));
          const maxMm =
            max !== undefined ? Number(convertToMm(max.toString())) : undefined;
          if (maxMm !== undefined && newMmValue > maxMm) return;
          updateValueAndNotify(newDisplayValue.toString());
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          const newDisplayValue = roundToPrecision(
            currentDisplayValue - stepValue,
            precision,
          );
          // Convert to mm for min/max comparison
          const newMmValue = Number(convertToMm(newDisplayValue.toString()));
          const minMm =
            min !== undefined ? Number(convertToMm(min.toString())) : undefined;
          if (minMm !== undefined && newMmValue < minMm) return;
          updateValueAndNotify(newDisplayValue.toString());
        }
      },
      [
        step,
        preferredUnitSystem,
        max,
        min,
        displayValue,
        updateValueAndNotify,
        convertToMm,
      ],
    );

    const handleEnter = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          if (typeof displayValue === 'string') {
            const result = doEvaluation(displayValue);
            if (result) {
              e.preventDefault();
            }
          }
        }
      },
      [doEvaluation, displayValue],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
          case 'ArrowUp':
          case 'ArrowDown':
            handleArrowKeys(e);
            break;
          case 'Enter':
            handleEnter(e);
            break;
        }
      },
      [handleArrowKeys, handleEnter],
    );

    const onBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        _onBlur?.(e);
        const value = e.target.value;
        if (value) {
          doEvaluation(value);
        }
      },
      [_onBlur, doEvaluation],
    );

    const onChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        updateValueAndNotify(e.target.value);
      },
      [updateValueAndNotify],
    );

    return (
      <Input
        ref={forwardedRef}
        onBlur={onBlur}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        value={displayValue}
        defaultValue={convertedDefaultValue}
        {...inputProps}
      />
    );
  },
);

export type MathInputProps = InputGroupProps & {
  measure?: Measure;
  hasUnits?: boolean;
  rightAddonContent?: React.ReactNode;
  isRightAddonHidden?: boolean;
};

/**
 * A React component that renders an input group for mathematical values with unit display.
 *
 * @component
 * @param {MathInputProps} props - The input group properties, including children and measure type.
 * @param {React.Ref<HTMLDivElement>} forwardedRef - A forwarded ref to the underlying input group element.
 *
 * @remarks
 * - Wraps its children with a `MathInputProvider` to provide context for the preferred unit system and measure.
 * - Displays the appropriate unit as an addon, based on the user's preferred unit system and the specified measure.
 */
export const MathInput = forwardRef<HTMLDivElement, MathInputProps>(
  function MathInput(
    {
      children,
      measure = 'length',
      hasUnits = true,
      rightAddonContent,
      isRightAddonHidden = false,
      ...props
    },
    forwardedRef,
  ) {
    const { preferredUnitSystem } = usePreferredUnitSystem();

    const unit = UNITS_BY_SYSTEM_AND_MEASURE[preferredUnitSystem][measure];

    return (
      <MathInputProvider value={{ preferredUnitSystem, measure, hasUnits }}>
        <InputGroup ref={forwardedRef} {...props}>
          {children}
          {!isRightAddonHidden && (hasUnits || rightAddonContent) && (
            <InputRightAddon>
              {hasUnits && unit}
              {rightAddonContent}
            </InputRightAddon>
          )}
        </InputGroup>
      </MathInputProvider>
    );
  },
);
